import { resolveSemanticColor, type SemanticColorKey } from '@/theme/semanticTokens';
import {
  elevationTokens,
  type ElevationToken,
  type RadiusToken,
  type SpacingToken,
} from '@/theme/tokens';
import { r, s } from '@/theme/tokens';
import { Box } from './Box';
import { useTheme } from './hooks';
import type { SxProps, Theme } from './types';
import type { ReactElement, ReactNode } from 'react';

const SPACING_MAP: Record<SpacingToken, string> = {
  none: s('none'),
  xxxs: s('xxxs'),
  xxs: s('xxs'),
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
  '4xl': s('4xl'),
  '5xl': s('5xl'),
  gutter: s('gutter'),
  section: s('section'),
};

const RADIUS_MAP: Record<RadiusToken, string> = {
  none: r('none'),
  xs: r('xs'),
  sm: r('sm'),
  chip: r('chip'),
  md: r('md'),
  lg: r('lg'),
  card: r('card'),
  panel: r('panel'),
};

const resolveSpacing = (token: SpacingToken | null | undefined): string | undefined =>
  token ? SPACING_MAP[token] : undefined;

type SurfaceProps = {
  children: ReactNode;
  padding?: SpacingToken | null;
  radius?: RadiusToken;
  elevation?: ElevationToken;
  border?: boolean;
  background?: SurfaceBackground;
  sx?: SxProps<Theme>;
};

export type SurfaceBackground =
  | 'surface'
  | 'page'
  | 'primary'
  | 'secondary'
  | 'inset'
  | 'hover'
  | 'overlay'
  | 'transparent';

const SURFACE_BACKGROUND_COLOR_KEYS: Record<
  Exclude<SurfaceBackground, 'transparent'>,
  SemanticColorKey
> = {
  surface: 'surface',
  page: 'surfacePage',
  primary: 'surface',
  secondary: 'surfaceInset',
  inset: 'surfaceInset',
  hover: 'surfaceHover',
  overlay: 'surfaceOverlay',
};

const resolveSurfaceBackground = (theme: Theme, background: SurfaceBackground): string =>
  background === 'transparent'
    ? 'transparent'
    : `${resolveSemanticColor(theme, SURFACE_BACKGROUND_COLOR_KEYS[background])} !important`;

export const Surface = ({
  children,
  padding = 'md',
  radius = 'card',
  elevation = 'none',
  border = true,
  background = 'surface',
  sx,
}: SurfaceProps): ReactElement => {
  const theme = useTheme();
  const baseSx: SxProps<Theme> = {
    backgroundColor: resolveSurfaceBackground(theme, background),
    backgroundImage: 'none',
    borderRadius: RADIUS_MAP[radius],
    boxShadow: elevationTokens[elevation],
    border: border ? `1px solid ${resolveSemanticColor(theme, 'border')}` : 'none',
    boxSizing: 'border-box',
    opacity: 1,
    isolation: 'isolate',
    minWidth: 0,
    p: resolveSpacing(padding),
  };
  const resolvedSx = sx ? [baseSx, ...(Array.isArray(sx) ? sx : [sx])] : baseSx;

  return <Box sx={resolvedSx}>{children}</Box>;
};
